// components/DistrictPathwayMatrix.jsx
import { useMemo } from 'react';

/**
 * Normalize district name for matching
 */
function normalizeDistrictName(name) {
  return name
    .toLowerCase()
    .replace(/\s*(school district|sd|district|schools?)\s*/gi, '')
    .replace(/[^a-z0-9]/g, '')
    .trim();
}

/**
 * District Pathway Matrix
 * Transposed view: Districts as rows, CIP pathways as angled columns
 * Groups districts by region with section headers
 */
export default function DistrictPathwayMatrix({
  cipEnrolled,
  filterSession,
  nontrad,
  yearIndex = 4,
  regionalGroups = {},
  regionalColors = {}
}) {
  // Build normalized lookup map for regions
  const regionLookup = useMemo(() => {
    const lookup = new Map();
    Object.entries(regionalGroups).forEach(([groupName, schools]) => {
      schools.forEach(school => {
        lookup.set(normalizeDistrictName(school), groupName);
      });
    });
    return lookup;
  }, [regionalGroups]);

  // Process data into transposed matrix format
  const matrixData = useMemo(() => {
    if (!cipEnrolled?.length) return { pathways: [], matrix: {}, regions: [] };

    // Helper to check if a CIP matches the current filter
    const matchesFilter = (item) => {
      if (filterSession === "nontrad-female") {
        return nontrad?.female?.includes(item.cipcode);
      } else if (filterSession === "nontrad-male") {
        return nontrad?.male?.includes(item.cipcode);
      }
      return item.clusterName === filterSession || item.programArea === filterSession;
    };

    // Get district's regional group
    const getRegion = (schoolName) => {
      const normalized = normalizeDistrictName(schoolName);
      if (regionLookup.has(normalized)) {
        return regionLookup.get(normalized);
      }
      for (const [normalizedSchool, groupName] of regionLookup.entries()) {
        if (normalized.startsWith(normalizedSchool) || normalizedSchool.startsWith(normalized)) {
          return groupName;
        }
      }
      return "Other";
    };

    const filteredCips = cipEnrolled.filter(matchesFilter);

    // Build pathway list (columns)
    const pathwayMap = new Map();
    filteredCips.forEach(cip => {
      if (!pathwayMap.has(cip.cipcode)) {
        pathwayMap.set(cip.cipcode, {
          code: cip.cipcode,
          name: cip.cipcodeName,
          totalEnrollment: 0
        });
      }
    });

    // Build district list and matrix
    const districtSet = new Set();
    const matrix = {}; // { districtName: { cipcode: enrollment } }

    filteredCips.forEach(cip => {
      if (!cip.enrolled) return;

      cip.enrolled.forEach(school => {
        if (school.schoolName === "Total") return;

        const enrollment = school.enrollment?.[yearIndex] ?? 0;
        districtSet.add(school.schoolName);

        if (!matrix[school.schoolName]) {
          matrix[school.schoolName] = {};
        }

        if (enrollment > 0) {
          matrix[school.schoolName][cip.cipcode] = enrollment;
          pathwayMap.get(cip.cipcode).totalEnrollment += enrollment;
        }
      });
    });

    // Sort pathways by total enrollment (most popular first)
    const pathways = Array.from(pathwayMap.values())
      .filter(p => p.totalEnrollment > 0)
      .sort((a, b) => b.totalEnrollment - a.totalEnrollment);

    // Group districts by region - include ALL districts from regionalGroups
    const districtsByRegion = {};
    Object.entries(regionalGroups).forEach(([region, districts]) => {
      districtsByRegion[region] = [...districts].sort((a, b) => a.localeCompare(b));
    });

    // Track "Other" districts (in data but not in regionalGroups)
    districtsByRegion["Other"] = [];
    districtSet.forEach(district => {
      const region = getRegion(district);
      if (region === "Other") {
        districtsByRegion["Other"].push(district);
      }
    });
    districtsByRegion["Other"].sort((a, b) => a.localeCompare(b));

    // Build ordered region list - include all regions from config
    const regions = Object.entries(regionalGroups)
      .map(([name]) => ({
        name,
        color: regionalColors[name] || '#6b7280',
        districts: districtsByRegion[name] || []
      }));

    // Add "Other" if it has districts
    if (districtsByRegion["Other"]?.length > 0) {
      regions.push({
        name: "Other",
        color: '#6b7280',
        districts: districtsByRegion["Other"]
      });
    }

    return { pathways, matrix, regions };
  }, [cipEnrolled, filterSession, nontrad, yearIndex, regionalGroups, regionalColors, regionLookup]);

  // Calculate opportunity data - districts with zero participation across all pathways
  const opportunityData = useMemo(() => {
    const allDistricts = matrixData.regions.flatMap(r => r.districts);
    const districtsWithZero = allDistricts.filter(district => {
      const districtData = matrixData.matrix[district] || {};
      return !matrixData.pathways.some(p => districtData[p.code] > 0);
    });
    return {
      total: allDistricts.length,
      withZero: districtsWithZero,
      count: districtsWithZero.length
    };
  }, [matrixData]);

  if (!matrixData?.pathways?.length) {
    return (
      <div className="p-12 text-center border rounded-lg text-zinc-500">
        No enrollment data found.
      </div>
    );
  }

  const COL_WIDTH = 40;
  const LABEL_HEIGHT = 140;

  return (
    <div className="max-w-5xl bg-white border border-zinc-200 rounded-lg shadow-sm flex flex-col max-h-[800px]">

      <div className="overflow-auto flex-1">
        <table className="w-full text-xs border-collapse" style={{ tableLayout: 'fixed' }}>
          <thead>
            <tr className="bg-zinc-50 border-b border-zinc-200">
              {/* Corner Cell - sticky to both top and left */}
              <th
                className="sticky left-0 top-0 z-30 bg-zinc-50 border-r border-zinc-200"
                style={{ width: 180, minWidth: 180 }}
              />

              {/* Angled Headers - sticky top */}
              {matrixData.pathways.map(pathway => (
                <th
                  key={pathway.code}
                  className="sticky top-0 z-20 p-0 border-r border-zinc-100 last:border-0 bg-zinc-50"
                  style={{
                    width: COL_WIDTH,
                    minWidth: COL_WIDTH,
                    height: LABEL_HEIGHT
                  }}
                >
                  <div
                    className="absolute bottom-2 left-1/2 -translate-x-1/2 whitespace-nowrap font-medium text-[11px] text-zinc-600"
                    style={{
                      writingMode: 'vertical-rl',
                      textOrientation: 'mixed',
                      transform: 'translateX(-50%) rotate(180deg)',
                      maxHeight: LABEL_HEIGHT - 10,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis'
                    }}
                    title={pathway.name}
                  >
                    {pathway.name}
                  </div>
                </th>
              ))}
            </tr>
          </thead>

          <tbody>
            {matrixData.regions.map(region => (
              <>
                {/* Region Header Row */}
                <tr key={`header-${region.name}`} className="border-b border-zinc-600">
                  <td
                    className="sticky left-0 z-10 px-3 py-2 font-semibold text-zinc-800 bg-white border-r border-zinc-200"
                  >
                    <div className="flex items-center gap-2">
                      <span>{region.name}</span>
                      <span className="text-zinc-400 font-normal">({region.districts.length})</span>
                    </div>
                  </td>
                  {matrixData.pathways.map(p => (
                    <td key={p.code} className="bg-zinc-50/30" />
                  ))}
                </tr>

                {/* District Data Rows */}
                {region.districts.map((district, idx) => {
                  const districtData = matrixData.matrix[district] || {};
                  const hasAnyOffering = matrixData.pathways.some(p => districtData[p.code]);
                  const rowClass = idx % 2 === 0 ? 'bg-white' : 'bg-zinc-50/30';

                  return (
                    <tr
                      key={district}
                      className={`group border-b border-zinc-100 hover:bg-zinc-100 transition-colors ${rowClass} ${!hasAnyOffering ? 'opacity-50' : ''}`}
                    >
                      {/* District Column - sticky left */}
                      <td
                        className={`px-3 py-1.5 text-zinc-700 sticky left-0 z-10 border-r border-zinc-200 group-hover:bg-zinc-100 transition-colors ${rowClass}`}
                        title={district}
                      >
                        <div className="truncate" style={{ maxWidth: 160 }}>
                          {district}
                        </div>
                      </td>

                      {matrixData.pathways.map(pathway => {
                        const enrollment = districtData[pathway.code];
                        return (
                          <td
                            key={pathway.code}
                            className="text-center py-1.5 border-r border-zinc-50 last:border-0"
                            style={{ width: COL_WIDTH }}
                          >
                            {enrollment ? (
                              <span className="inline-block font-medium text-zinc-700">
                                {enrollment}
                              </span>
                            ) : (
                              <span className="text-zinc-300">—</span>
                            )}
                          </td>
                        );
                      })}
                    </tr>
                  );
                })}
              </>
            ))}
          </tbody>
        </table>
      </div>

      {/* Opportunity Callout */}
      {opportunityData.count > 0 && (
        <div className="bg-amber-50 border-t border-amber-200 px-4 py-3 shrink-0">
          <div className="flex items-start gap-2 text-sm">
            <svg className="w-5 h-5 text-amber-600 mt-0.5 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <div>
              <span className="font-bold text-amber-800">
                Opportunity ({opportunityData.count} of {opportunityData.total} districts):
              </span>{' '}
              <span className="text-amber-700">
                {opportunityData.withZero.join(', ')} {opportunityData.count === 1 ? 'has' : 'have'} no CIP enrollments in this area.
              </span>
            </div>
          </div>
        </div>
      )}

      {/* Footer */}
      <div className="bg-zinc-50 border-t border-zinc-200 px-4 py-2 text-xs text-zinc-500 flex justify-between shrink-0">
         <span>CIP Enrollments (2023-24)</span>
         <span>{matrixData.pathways.length} pathways · {matrixData.regions.reduce((acc, r) => acc + r.districts.length, 0)} districts</span>
      </div>
    </div>
  );
}
